import _ from 'lodash';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { debounceTime, filter, map, switchMap } from 'rxjs/operators';
import { ImageService } from 'app/core/services/image.service';
import { select, Store } from '@ngrx/store';
import { selectCategoryList, selectSubCategoryList } from '../../features/categories/categories.selectors';
import { LoadCategories, SelectCategory } from '../../features/categories/categories.actions';
import { AddInfo } from '../../features/data/data.actions';
import { selectAddInfoError } from '../../features/data/data.selectors';
import { ERRORS } from 'app/virtual-event/virtual-event.enums';
import { HandleService } from '../../services/handle.service';
import { createMask } from '@ngneat/input-mask';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-signup02',
  templateUrl: './signup02.component.html',
  styleUrls: ['./signup02.component.scss']
})
export class Signup02Component implements OnInit {
  destroyRef = inject(DestroyRef)
  public error$ = this.store.pipe(
    select(selectAddInfoError)
  );

  public categories$ = this.store.pipe(
    select(selectCategoryList)
  )

  public subcategories$ = this.store.pipe(
    select(selectSubCategoryList)
  )

  public coverImage: UntypedFormGroup = new UntypedFormGroup({
    file: new UntypedFormControl(''),
    dataUrl: new UntypedFormControl('/images/blank_2x1.png'),
    url: new UntypedFormControl('/images/blank_2x1.png'),
  });

  public logoImage: UntypedFormGroup = new UntypedFormGroup({
    file: new UntypedFormControl(''),
    dataUrl: new UntypedFormControl('/images/grey_1x1.png'),
    url: new UntypedFormControl('/images/grey_1x1.png'),
  });

  public form: UntypedFormGroup = new UntypedFormGroup({
    imageUrl: new UntypedFormControl(''),
    logoUrl: new UntypedFormControl(''),
    handle: new UntypedFormControl('', [Validators.required, Validators.pattern(/^[a-zA-Z][a-zA-Z0-9_\-]+$/)]),  
    topCategoryId: new UntypedFormControl('', [Validators.required]),
    secondCategoryId: new UntypedFormControl('', [Validators.required]),
    purpose: new UntypedFormControl('', [Validators.required]),
    desc: new UntypedFormControl(''),
    phone: new UntypedFormControl('', [Validators.required]),
    supportEmail: new UntypedFormControl('', [Validators.email])
  });
  public phoneInputMask = createMask("+1 (999) 9999999");

  constructor(
    private store: Store,
    private imageService: ImageService,
    private domSanitizer: DomSanitizer,
    private handleService: HandleService,
  ) { }

  ngOnInit(): void {
    const { form } = this;
    const handle = form.get('handle');
    handle.valueChanges.pipe(
      debounceTime(1000),
      switchMap(value => {
        return this.handleService.checkHandle(value);
      }),
      map(result => {
        const value = _.get(result, 'good', false);
        return !value;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      handle.markAsTouched();
      return value ?
        handle.setErrors({ unique: true }):
        handle.updateValueAndValidity({ emitEvent: false });
    })

    this.error$.pipe(
      filter(error => !_.isNull(error)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.form.get('handle').setErrors({ unique: true });
    });

    this.store.dispatch({
      type: LoadCategories.type
    });

    this.coverImage.get('file').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      const windowURL = window.URL || window.webkitURL;
      const url = windowURL.createObjectURL(value);
      this.imageService.cropper({
        aspectRatio: 2/1,
        url: this.domSanitizer.bypassSecurityTrustUrl(url),
        id: 'cover-image',
        name: value.name,
        type: value.type
      });
    });

    this.coverImage.get('url').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.form.get('imageUrl').setValue(value);
    });

    this.logoImage.get('file').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      const windowURL = window.URL || window.webkitURL;
      const url = windowURL.createObjectURL(value);
      this.imageService.cropper({
        aspectRatio: 1,
        url: this.domSanitizer.bypassSecurityTrustUrl(url),
        id: 'logo-image',
        name: value.name,
        type: value.type
      });
    });

    this.logoImage.get('url').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.form.get('logoUrl').setValue(value);
    });

    this.form.get('topCategoryId').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(category => {
      this.store.dispatch({
        type: SelectCategory.type,
        payload: category
      });
      this.form.get('secondCategoryId').setValue('');
    });

    this.imageService.imageDataUrlSubject.pipe(
      filter(({ id }) => id === 'cover-image'),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ result }) => {
      this.coverImage.get('dataUrl').setValue(result);
    });

    this.imageService.imageDataUrlSubject.pipe(
      filter(({ id }) => id === 'logo-image'),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ result }) => {
      this.logoImage.get('dataUrl').setValue(result);
    });

    this.imageService.imageSubject.pipe(
      filter(({ id }) => id === 'cover-image'),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ result }) => {
      if(result === false) {
        this.resetCoverImage();
      } else {
        this.coverImage.get('url').setValue(result.imageUrl);
      }
    });

    this.imageService.imageSubject.pipe(
      filter(({ id }) => id === 'logo-image'),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ result }) => {
      if(result === false) {
        this.resetCoverImage();
      } else {
        this.logoImage.get('url').setValue(result.imageUrl);
      }
    });
  }

  resetCoverImage() {
    this.coverImage.get('file').setValue(null);
    this.coverImage.get('dataUrl').setValue('/images/blank_2x1.png');
    this.coverImage.get('url').setValue('/images/blank_2x1.png');
  }

  resetLogoImage() {
    this.logoImage.get('file').setValue(null);
    this.logoImage.get('dataUrl').setValue('/images/grey_1x1.png');
    this.logoImage.get('url').setValue('/images/grey_1x1.png');
  }

  getHandleErrorMessage() {
    const { form } = this;
    const handle = form.get('handle');

    if(!handle.touched) return '';

    if(handle.hasError('required')) {
      return 'Please provide Unique Handle';
    }

    if(handle.hasError('pattern')) {
      return 'Handle can only contain a-z, 0-9, _ and -, must start with letter, no spaces';
    }

    if(handle.hasError('unique') && handle.value !=='') {
      return ERRORS.HandleAlreadyExist;
    }
    if(handle.hasError('unique') && handle.value =='') {   
      return 'Please provide Unique Handle';
    }

    return '';
  }

  onNext() {
    this.form.markAllAsTouched();

    if(this.form.invalid) {
      return;
    }

    this.store.dispatch({
      type: AddInfo.type,
      payload: this.form.value
    });
  }
}
